import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  TextareaHTMLAttributes,
} from 'react'
import { useId } from 'react'

import { t } from '../i18n'

// 'danger' is for the actions that cannot be undone. A destructive button that
// looks like every other button is a button somebody presses by muscle memory.
type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' 

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant
  readonly block?: boolean
  readonly busy?: boolean
}

export function Button({
  variant = 'primary',
  block = false,
  busy = false,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  const classes = ['button', `button--${variant}`]
  if (block) classes.push('button--block')
  if (className !== undefined) classes.push(className)

  return (
    <button
      type="button"
      {...rest}
      className={classes.join(' ')}
      disabled={disabled === true || busy}
      aria-busy={busy}
    >
      {busy ? <span className="spinner" aria-hidden="true" /> : null}
      {children}
    </button>
  )
}

interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  readonly label: string
  readonly error?: string | undefined
}

export function TextField({ label, error, ...rest }: TextFieldProps) {
  const id = useId()
  const errorId = `${id}-error`
  const invalid = error !== undefined && error !== ''

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <input
        {...rest}
        id={id}
        className="field__input"
        aria-invalid={invalid}
        {...(invalid ? { 'aria-describedby': errorId } : {})}
      />
      {invalid ? (
        <span className="field__error" id={errorId}>
          {error}
        </span>
      ) : null}
    </div>
  )
}

export function Alert({
  tone = 'error',
  children,
}: {
  readonly tone?: 'error' | 'warning' | 'info'
  readonly children: ReactNode
}) {
  return (
    // `alert` announces itself to a screen reader the moment it appears, which
    // is what a sign-in failure needs — the person may not be looking at the spot
    // where the message lands.
    <div className={`alert alert--${tone}`} role="alert">
      {children}
    </div>
  )
}

type BadgeTone = 'neutral' | 'accent' | 'warning' | 'danger' | 'muted'

/**
 * A short piece of state, coloured by how much it should interrupt.
 *
 * The default is deliberately quiet. A screen where every badge shouts is a
 * screen where the one that matters — a draft waiting, a conversation handed to
 * a person — does not stand out at all.
 */
export function Badge({
  tone = 'neutral',
  title,
  children,
}: {
  readonly tone?: BadgeTone
  readonly title?: string
  readonly children: ReactNode
}) {
  return (
    <span className={`badge badge--${tone}`} {...(title === undefined ? {} : { title })}>
      {children}
    </span>
  )
}

interface TextAreaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> {
  readonly label: string
  readonly hint?: ReactNode
  readonly error?: string | undefined
}

export function TextArea({ label, hint, error, className, ...rest }: TextAreaProps) {
  const id = useId()
  const errorId = `${id}-error`
  const invalid = error !== undefined && error !== ''

  return (
    <div className="field">
      <div className="field__row">
        <label className="field__label" htmlFor={id}>
          {label}
        </label>
        {hint === undefined ? null : <span className="field__hint">{hint}</span>}
      </div>
      <textarea
        {...rest}
        id={id}
        className={className === undefined ? 'field__textarea' : `field__textarea ${className}`}
        aria-invalid={invalid}
        {...(invalid ? { 'aria-describedby': errorId } : {})}
      />
      {invalid ? (
        <span className="field__error" id={errorId}>
          {error}
        </span>
      ) : null}
    </div>
  )
}

/** A label and a value on one dense line. The panel is made of these. */
export function Detail({
  term,
  children,
}: {
  readonly term: string
  readonly children: ReactNode
}) {
  return (
    <div className="detail">
      <dt className="detail__term">{term}</dt>
      <dd className="detail__value">{children}</dd>
    </div>
  )
}

/** An inline "still loading" that does not move the layout. */
export function InlineLoader({ label = t('common.loading') }: { readonly label?: string }) {
  return (
    <span className="inline-loader">
      <span className="spinner" aria-hidden="true" />
      {label}
    </span>
  )
}

export function Panel({
  className,
  children,
}: {
  readonly className?: string
  readonly children: ReactNode
}) {
  return <div className={className === undefined ? 'panel' : `panel ${className}`}>{children}</div>
}

/** A whole-screen "we are still finding out" state. */
export function FullScreenLoader({ label = t('common.loading') }: { readonly label?: string }) {
  return (
    <div className="centered">
      <div className="centered__inner">
        <span className="spinner" aria-hidden="true" />
        <span>{label}</span>
      </div>
    </div>
  )
}

export function PageHeader({
  title,
  badge,
}: {
  readonly title: string
  readonly badge?: ReactNode
}) {
  return (
    <header className="page-header">
      <h1 className="page-header__title">{title}</h1>
      {badge}
    </header>
  )
}
